/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { ControlledRateLimitModal } from './ControlledRateLimitModal';
import { useRateLimit } from './useRateLimit';
import { useRateLimitMountWarnings } from './useRateLimitMountWarnings';
import { useRateLimitReviewTrigger } from './useRateLimitReviewTrigger';

/**
 * Standard notice shown when the API rate limits the user with a 429.
 *
 * Driven entirely by module-level rate-limit state, so it needs no props, no
 * redux state and no wiring from the screen that made the request. Already
 * mounted for apps using `ExternalAppProviders`; those apps only need to call
 * `registerRateLimitInterceptor` at startup, or `reportRateLimit` from their own
 * error handling.
 *
 * All of the notice's behavior lives in `ControlledRateLimitModal`, so a service
 * driving that variant from its own state gets the identical notice.
 *
 * Because it mounts on its own but is only raised by a registration the owning team
 * has to make, it warns at dev time when it is mounted inert or mounted twice —
 * both of which look like nothing happening.
 *
 * Mounting it everywhere is also what lets a URL summon it for review on any page of
 * a deployed non-production environment — see `useRateLimitReviewTrigger`.
 */
export function RateLimitModal() {
    const { isRateLimited, retryAfterMs, dismiss } = useRateLimit();

    useRateLimitMountWarnings();
    useRateLimitReviewTrigger();

    return (
        <ControlledRateLimitModal
            isOpen={isRateLimited}
            retryAfterMs={retryAfterMs}
            onAcknowledge={dismiss}
        />
    );
}
